import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Check, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useXeroConnection, useXeroSyncStats } from '@/hooks/useXeroConnection';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatInTz } from '@/utils/timezone';
import { XeroTenantPickerDialog } from './XeroTenantPickerDialog';
import { XeroMappingForm } from './XeroMappingForm';
import { XeroSyncIssuesList } from './XeroSyncIssuesList';
import { XeroBackfillDialog } from './XeroBackfillDialog';

const OAUTH_RETURN_REASONS: Record<string, string> = {
  currency_unsupported:
    "That Xero organisation will not accept the currency this company invoices in. Enable it in Xero under Settings, then Currencies. That needs a Xero plan which includes multicurrency.",
  currency_mismatch:
    "That Xero organisation keeps its books in a different currency from the one this company invoices in. Connect the organisation that matches.",
  tenant_already_connected: 'That Xero organisation is already connected to another company.',
  no_organisations: 'That Xero login has no organisations we can connect to.',
  storage_failed:
    "We received Xero's authorisation but failed to save the connection. Try again, and contact support if it persists.",
  access_denied: 'You declined the Xero consent screen. No connection was created.',
  invalid_state: 'The sign-in attempt could not be verified. Start the connection again from this page.',
  state_already_used: 'That sign-in link was already used. Start the connection again from this page.',
  state_expired: 'The sign-in attempt timed out. Start the connection again from this page.',
  state_user_mismatch: 'The connection was started by a different user. Start it again from your own login.',
  server_misconfigured: 'Xero is not configured on the server yet. Contact support.',
  missing_params: 'Xero did not return an authorisation code. Try connecting again.',
};

const ENABLES = [
  'Every invoice you send lands in Xero as an approved sales invoice',
  'Cash, card, transfer and Square payments recorded here are applied in Xero',
  'Payments your bookkeeper records in Xero mark the invoice paid here',
  'Refunds become credit notes; customers stay in step as contacts',
];

/** Settings body for the Xero integration (OAuth connect, organisation picker, mapping, sync health). */
export function XeroIntegrationPanel() {
  const {
    integration,
    isForbidden,
    isLoading,
    refetch,
    connect,
    isConnecting,
    disconnect,
    isDisconnecting,
    setActiveTenant,
    isSettingTenant,
  } = useXeroConnection();
  const { formatShortDate, locale, timezone } = useCompanyLocale();
  const formatDateTime = (value: string) => formatInTz(new Date(value), timezone, `${locale.shortDate} HH:mm`);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [setupComplete, setSetupComplete] = useState<boolean | null>(null);

  const isConnected = integration?.status === 'connected';
  const hasTenant = Boolean(integration?.metadata.tenant_id);
  const stats = useXeroSyncStats(isConnected && hasTenant);

  // Read the OAuth-return query string written by XeroCallback, show a toast,
  // then strip the params so a refresh doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('xero');
    if (!status) return;

    if (status === 'connected') {
      toast({ title: 'Xero connected' });
      if (params.get('multiple_tenants') === '1') setPickerOpen(true);
      void refetch();
    } else if (status === 'error') {
      const reason = params.get('reason') ?? '';
      const friendly = OAUTH_RETURN_REASONS[reason] ?? (reason || 'Unknown error');
      toast({ title: 'Xero connection failed', description: friendly, variant: 'destructive' });
    }

    params.delete('xero');
    params.delete('reason');
    params.delete('multiple_tenants');
    const next = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (next ? `?${next}` : ''));
  }, [refetch]);

  const handleConnect = async () => {
    try {
      await connect();
    } catch (e) {
      toast({ title: 'Could not start Xero connection', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      toast({ title: 'Xero disconnected', description: 'Nothing new will sync. Reconnect the same organisation later and it picks up where it left off.' });
    } catch (e) {
      toast({ title: 'Disconnect failed', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const isErrored = integration?.status === 'error' || integration?.status === 'expired';
  const tenants = integration?.metadata.available_tenants ?? [];
  const hasMultipleTenants = tenants.length > 1;
  const org = integration?.metadata.org;
  const queue = stats.data?.queue;
  const links = stats.data?.links;
  const poll = stats.data?.poll;
  const attention = (queue?.failed ?? 0) + (queue?.dead ?? 0) + (links?.drift ?? 0) + (links?.locked ?? 0) + (links?.orphaned ?? 0);

  return (
    <>
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Keep your books in Xero without double entry. Invoices, payments and customers flow from Steddy to
          Xero; payments and voids your bookkeeper records in Xero flow back.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status...
          </div>
        ) : isForbidden ? (
          <div className="flex gap-2 rounded-lg border p-3 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="text-muted-foreground">Only admins can connect or manage the Xero integration.</div>
          </div>
        ) : !integration || integration.status === 'revoked' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Click below to authorise Steddy with your Xero login. We ask only for what the sync needs: contacts,
              invoices, payments and your chart of accounts.
            </p>
            <Button onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? 'Redirecting to Xero...' : 'Connect Xero'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {isErrored && (
              <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <div className="font-medium">Connection needs attention</div>
                  <div className="text-muted-foreground">
                    {integration.status_message || integration.last_error || 'Reconnect to resume syncing.'}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {integration.metadata.tenant_name || org?.name || (hasTenant ? 'Xero organisation' : 'No organisation chosen yet')}
                </span>
                {org?.base_currency && (
                  <Badge variant="outline" className="text-[11px]">
                    {org.base_currency}
                  </Badge>
                )}
              </div>
              {org?.base_currency && org.base_currency !== locale.currency && (
                <div className="text-xs text-muted-foreground">
                  Invoices are sent in {locale.currency}. This organisation keeps its books in {org.base_currency}, so
                  Xero converts each one at its daily rate.
                </div>
              )}
              {integration.metadata.xero_user_email && (
                <div className="text-xs text-muted-foreground">Authorised by {integration.metadata.xero_user_email}</div>
              )}
              <div className="text-xs text-muted-foreground">
                Connected {formatShortDate(integration.connected_at)}
                {integration.last_refreshed_at ? ` · token refreshed ${formatDateTime(integration.last_refreshed_at)}` : ''}
              </div>

              {/* Connection actions belong with the connection, not below the
                  mapping form where they were easy to miss entirely. */}
              <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                {/* One affordance, not two. When several organisations are
                    already authorised the picker is instant; otherwise this
                    goes to Xero to authorise one, which the picker also
                    offers. "Switch" and "Change" side by side read as the
                    same thing and only made people guess. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={hasMultipleTenants && hasTenant ? () => setPickerOpen(true) : handleConnect}
                  disabled={isConnecting}
                >
                  {isConnecting ? 'Redirecting...' : isErrored ? 'Reconnect' : 'Change organisation'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDisconnect(true)}
                  disabled={isDisconnecting}
                  className="ml-auto text-destructive hover:text-destructive"
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </div>
            </div>

            {!hasTenant && tenants.length > 0 && (
              <div className="flex gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <div className="flex-1">
                  <div className="font-medium">Choose the organisation to sync</div>
                  <div className="text-muted-foreground">Your Xero login reaches more than one organisation.</div>
                </div>
                <Button size="sm" onClick={() => setPickerOpen(true)}>
                  Choose organisation
                </Button>
              </div>
            )}

            {isConnected && hasTenant && (
              <div className="space-y-4 border-t pt-4">
                <XeroMappingForm
                  enabled={isConnected && hasTenant}
                  onSaved={(complete) => {
                    setSetupComplete(complete);
                    void stats.refetch();
                  }}
                  onLoaded={(complete) => setSetupComplete(complete)}
                />
              </div>
            )}

            {isConnected && hasTenant && stats.data && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Sync health</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Synced (7 days)" value={queue?.done ?? 0} />
                  <Stat label="Waiting" value={(queue?.queued ?? 0) + (queue?.running ?? 0)} />
                  <Stat label="Needs attention" value={attention} tone={attention > 0 ? 'warning' : undefined} />
                  <Stat label="In Xero" value={(links?.invoices ?? 0)} hint={`${links?.contacts ?? 0} contacts · ${links?.payments ?? 0} payments`} />
                </div>
                <div className="text-xs text-muted-foreground">
                  {poll?.last_run_at
                    ? `Last checked Xero ${formatDateTime(poll.last_run_at)}${poll.last_run_status && poll.last_run_status !== 'ok' ? ` (${poll.last_run_status})` : ''}`
                    : 'Xero has not been checked yet.'}
                  {poll?.rate_limited_until && new Date(poll.rate_limited_until) > new Date()
                    ? ` · paused by Xero's rate limit until ${formatDateTime(poll.rate_limited_until)}`
                    : ''}
                </div>
                <XeroSyncIssuesList enabled={isConnected && hasTenant} />
                {setupComplete && (
                  <div>
                    <Button variant="outline" size="sm" onClick={() => setBackfillOpen(true)}>
                      Sync history…
                    </Button>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Only invoices issued after connecting are sent automatically. Send older ones from a date you choose.
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">What this enables</p>
          <ul className="space-y-1.5 text-sm">
            {ENABLES.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span className="text-muted-foreground">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <XeroTenantPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        tenants={tenants}
        currentTenantId={integration?.metadata.tenant_id ?? null}
        onSelect={setActiveTenant}
        isSaving={isSettingTenant}
        onAuthoriseAnother={handleConnect}
        isAuthorising={isConnecting}
      />
      <XeroBackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen} />

      <AlertDialog open={confirmDisconnect} onOpenChange={(next) => !isDisconnecting && setConfirmDisconnect(next)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect {integration?.metadata.tenant_name || org?.name || 'Xero'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              New invoices and payments stop syncing straight away, and anything still queued is dropped. Nothing
              already in Xero changes. Reconnecting the same organisation later picks up where it left off without
              creating duplicates, because the link between each invoice and its Xero copy is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Stay connected</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDisconnect().finally(() => setConfirmDisconnect(false));
              }}
              disabled={isDisconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: number; hint?: string; tone?: 'warning' }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${tone === 'warning' ? 'text-warning' : ''}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
